/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { SalespersonBudgetType } from '@gh/builder-shared-data';
import {
  createGroupIndexDriver,
  createTableDriver,
  isEntityInvalid,
  Table,
  whenDirty,
  whenExisting,
} from '@gh/common-shared';
import { ID, WithDirty, WithFormMode } from '@gh/common-shared-data';
import { Enum } from '@gh/core-data';
import { FormMode } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { composeComparators, currentYear, valueComparator } from '@gh/core-util';
import { first, flatMap } from 'lodash';
import { calculateSbListItemGroupKey, getSbListItemId, SB_YEARS, SbListItem } from '../../resources/sb-list.model';

export enum SbListStateEventType {
  Load,
  FilterSetField,
  ItemAddSet, ItemRemove, ItemSetField,
  ResetSelectedId, ResetFocusedCell, NavigateToInvalid,
}

export type SbListStateLoadEvent = { type: SbListStateEventType.Load; items: SbListItem[]; mode: FormMode };
export type SbListStateFilterSetFieldEvent = {
  type: SbListStateEventType.FilterSetField;
  name: keyof SbFilter;
  value: any;
};
export type SbListStateItemAddEvent = {
  type: SbListStateEventType.ItemAddSet;
  items: SbListItem[];
};
export type SbListStateItemRemoveEvent = { type: SbListStateEventType.ItemRemove; itemId: ID };
export type SbListStateItemSetFieldEvent = {
  type: SbListStateEventType.ItemSetField;
  itemId: ID;
  name: string;
  value: any;
};

export type SbListStateResetSelectedIdEvent = { type: SbListStateEventType.ResetSelectedId };
export type SbListStateResetFocusedCellEvent = { type: SbListStateEventType.ResetFocusedCell };
export type SbListStateResetNavigateToInvalidEvent = { type: SbListStateEventType.NavigateToInvalid };

export type SbListStateEvent = SbListStateLoadEvent
  | SbListStateFilterSetFieldEvent
  | SbListStateItemAddEvent | SbListStateItemRemoveEvent | SbListStateItemSetFieldEvent
  | SbListStateResetSelectedIdEvent | SbListStateResetFocusedCellEvent | SbListStateResetNavigateToInvalidEvent;

export type SbFilter = {
  year: number;
  type: SalespersonBudgetType;
};

export type SbListSelection = {
  itemId?: ID;
  itemCell?: FocusedCell;
};

export type SbListState = WithFormMode<WithDirty<{
  originalItems: SbListItem[];
  filter: SbFilter;
  items: Table<SbListItem>;
  selection: SbListSelection;
}>>;

export const sbItemComparator = composeComparators(valueComparator('name'), valueComparator(getSbListItemId));

export const sbItemTable = createTableDriver(
  getSbListItemId,
  createGroupIndexDriver<SbListItem>(
    'typeAndYear',
    getSbListItemId,
    whenExisting,
    (item) => item.group,
    sbItemComparator));

export const SB_LIST_INIT_STATE: SbListState = {
  originalItems: [],
  formMode: FormMode.ViewMode,
  filter: { type: SalespersonBudgetType.Leads, year: currentYear() },
  items: sbItemTable.create(),
  isDirty: false,
  selection: {},
};

const CURRENT_YEAR = currentYear();

export const querySbFilter = (state: SbListState) => state.filter;
export const querySbItemsByGroup = ({ items }: SbListState, type: SalespersonBudgetType, year: number) =>
  sbItemTable.byGroup(items, 'typeAndYear', calculateSbListItemGroupKey(type, year));
export const querySbCurrentItems = (state: SbListState) =>
  querySbItemsByGroup(state, state.filter.type, state.filter.year);
export const querySbSelectedItemId = ({ selection: { itemId } }: SbListState) => itemId;
export const querySbSelectedFocusedCell = ({ selection: { itemCell } }: SbListState) => itemCell;
export const querySbInvalidItems = ({ items }: SbListState) => sbItemTable.all(items).filter(isEntityInvalid);
export const querySbFirstInvalidItem = (state: SbListState) => {
  const { type: filteredType, year: filteredYear } = state.filter;

  // build all combinations of salesperson budget type and year (type-year)
  const groups = <[SalespersonBudgetType, number][]>flatMap(
    Enum.names(SalespersonBudgetType),
    (type) => SB_YEARS.map((year) => [type, year]));

  // build array of all type-year combinations without currently selected combination
  const anotherGroups = groups.filter(([type, year]) => type !== filteredType || year !== filteredYear);
  // put currently selected type-year combination as the first one and find first combination having invalid items
  return first([[filteredType, filteredYear]].concat(anotherGroups).reduce(
    (invalidItems, [type, year]: [SalespersonBudgetType, number]) => {
      if (invalidItems.length > 0) {
        return invalidItems;
      }

      return querySbItemsByGroup(state, type, year).filter(isEntityInvalid);
    },
    <SbListItem[]>[]));
};
export const querySbHasErrors = (state: SbListState) => querySbInvalidItems(state).length > 0;
export const querySbSaveModel = ({ items }: SbListState) => sbItemTable.all(items).filter(whenDirty);
export const querySbOriginalItems = ({ originalItems }: SbListState) => originalItems;
